// frontend/src/pages/UPCMigrationPage.js
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  FormControlLabel,
  Checkbox,
  Divider,
  Card,
  CardContent,
  LinearProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import GetAppIcon from '@mui/icons-material/GetApp';
import apiClient from '../api/client';

const UPCMigrationPage = () => {
  const [migrations, setMigrations] = useState([{ old_upc: '', new_upc: '', brand_override: '', description_override: '' }]);
  const [brandOverride, setBrandOverride] = useState('Private Selection');
  const [supplierNamesToStrip, setSupplierNamesToStrip] = useState('');
  const [createInSyndigo, setCreateInSyndigo] = useState(true);
  const [generateMRHI, setGenerateMRHI] = useState(true);
  const [allowOverwrite, setAllowOverwrite] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [partialSuccess, setPartialSuccess] = useState('');
  const [migrationHistory, setMigrationHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [migrationBatchId, setMigrationBatchId] = useState(null);
  const [migrationProgress, setMigrationProgress] = useState(null);
  const [migrationPolling, setMigrationPolling] = useState(false);

  const pollMigrationProgress = async (batchId) => {
    if (migrationPolling) return;
    
    setMigrationPolling(true);
    const pollInterval = setInterval(async () => {
      try {
        const response = await apiClient.get(`/upc-migration/batch/${batchId}/migration-status`);
        const data = response.data;
        
        setMigrationProgress(data);
        
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(pollInterval);
          setMigrationPolling(false);
          setIsProcessing(false);
          
          if (data.status === 'completed') {
            // Load the results
            const resultsResponse = await apiClient.get(`/upc-migration/batch/${batchId}/results`);
            setResults(resultsResponse.data);
            
            const total = data.total_count;
            const successCount = data.success_count || 0;
            const partialCount = data.partial_count || 0;
            const failedCount = data.failed_count || 0;
            
            if (failedCount === 0 && partialCount === 0) {
              setSuccessMessage(`✓ Migration complete: All ${successCount} migrations succeeded`);
            } else if (failedCount === total) {
              setError(`✗ Migration failed: All ${failedCount} migrations failed`);
            } else {
              setPartialSuccess(
                `⚠ Migration complete: ${successCount} full success, ${partialCount} partial, ${failedCount} failed`
              );
            }
          } else {
            setError(`Migration failed: ${data.error_message || 'Unknown error'}`);
          }
          
          setMigrationProgress(null);
        }
      } catch (err) {
        console.error('Error polling migration status:', err);
        clearInterval(pollInterval);
        setMigrationPolling(false);
        setIsProcessing(false);
      }
    }, 2000); // Poll every 2 seconds
    
    // Stop polling after 10 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      setMigrationPolling(false);
      if (isProcessing) {
        setError('Migration polling timeout. Please check the history for results.');
        setIsProcessing(false);
      }
    }, 600000);
  };

  const handleAddRow = () => {
    setMigrations([...migrations, { old_upc: '', new_upc: '', brand_override: '', description_override: '' }]);
  };

  const handleRemoveRow = (index) => {
    const newMigrations = migrations.filter((_, i) => i !== index);
    setMigrations(newMigrations.length ? newMigrations : [{ old_upc: '', new_upc: '', brand_override: '', description_override: '' }]);
  };

  const handleUpdateRow = (index, field, value) => {
    const newMigrations = [...migrations];
    newMigrations[index][field] = value;
    setMigrations(newMigrations);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const lines = text.split('\n');
        const newMigrations = [];
        
        // Skip header if it exists
        const startIndex = lines[0].toLowerCase().includes('old_upc') ? 1 : 0;
        
        for (let i = startIndex; i < lines.length; i++) {
          const line = lines[i].trim();
          if (line) {
            const parts = line.split(',').map(s => s.trim());
            const [old_upc, new_upc, brand_override = '', description_override = ''] = parts;
            if (old_upc && new_upc) {
              newMigrations.push({ old_upc, new_upc, brand_override, description_override });
            }
          }
        }
        
        if (newMigrations.length > 0) {
          setMigrations(newMigrations);
          setSuccessMessage(`Loaded ${newMigrations.length} UPC pairs from file`);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleDownloadTemplate = () => {
    const template = 'old_upc,new_upc,brand_override,description_override\n00000000000001,00000000000002,Private Selection,Optional custom description\n00000000000003,00000000000004,,';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'upc_migration_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleProcessMigration = async () => {
    setIsProcessing(true);
    setError('');
    setSuccessMessage('');
    setPartialSuccess('');
    setResults([]);
    
    // Filter out empty rows
    const validMigrations = migrations.filter(m => m.old_upc && m.new_upc);
    
    if (validMigrations.length === 0) {
      setError('Please enter at least one UPC pair');
      setIsProcessing(false);
      return;
    }
    
    try {
      // Parse supplier names to strip (comma-separated)
      const suppliersToStrip = supplierNamesToStrip
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);
      
      // Use async endpoint for large batches (more than 20 items)
      const useAsync = validMigrations.length > 20;
      
      if (useAsync) {
        // Use async migration for large batches
        const response = await apiClient.post('/upc-migration/migrate-async', {
          migrations: validMigrations,
          brand_override: brandOverride,
          strip_supplier_names: suppliersToStrip,
          create_in_syndigo: createInSyndigo,
          generate_mrhi: generateMRHI,
          allow_overwrite: allowOverwrite
        });
        
        const batchId = response.data.batch_id;
        setMigrationBatchId(batchId);
        setSuccessMessage(`Migration started for ${validMigrations.length} items. Processing...`);
        setError('');
        setPartialSuccess('');
        
        // Start polling for progress
        pollMigrationProgress(batchId);
        
      } else {
        // Use synchronous endpoint for small batches
        const response = await apiClient.post('/upc-migration/migrate', {
          migrations: validMigrations,
          brand_override: brandOverride,
          strip_supplier_names: suppliersToStrip,
          create_in_syndigo: createInSyndigo,
          generate_mrhi: generateMRHI,
          allow_overwrite: allowOverwrite
        });
        
        setResults(response.data);
        
        const total = response.data.length;
        const fullSuccessCount = response.data.filter(r => 
          r.status === 'success' && r.syndigo_created && r.image_id
        ).length;
        const partialSuccessCount = response.data.filter(r => 
          r.status === 'success' && (r.syndigo_created || r.image_id) && !(r.syndigo_created && r.image_id)
        ).length;
        const failedCount = response.data.filter(r => r.status === 'failed').length;
        
        // Determine overall status
        if (failedCount === 0 && partialSuccessCount === 0) {
          setSuccessMessage(
            `✓ Migration complete: All ${fullSuccessCount} migrations succeeded`
          );
        } else if (failedCount === total) {
          setError(
            `✗ Migration failed: All ${failedCount} migrations failed`
          );
          setSuccessMessage('');
        } else {
          // Mixed results - show as warning
          setPartialSuccess(
            `⚠ Migration partially complete: ${fullSuccessCount} full success, ${partialSuccessCount} partial, ${failedCount} failed`
          );
          setSuccessMessage('');
        }
      }
      
    } catch (err) {
      console.error('Migration error:', err);
      setError(err.response?.data?.detail || 'Failed to process migration');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExportResults = async () => {
    if (!results.length) {
      setError('No results to export');
      return;
    }

    try {
      // Use async export for large result sets
      if (results.length > 50) {
        setSuccessMessage('Starting Excel export. This may take a moment for large datasets...');
        
        // Start async export
        const exportResponse = await apiClient.post('/upc-migration/export-async', results);
        const exportId = exportResponse.data.export_id;
        
        // Poll for export completion
        let exportComplete = false;
        let pollCount = 0;
        const maxPolls = 60; // 2 minutes maximum
        
        while (!exportComplete && pollCount < maxPolls) {
          await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
          
          const statusResponse = await apiClient.get(`/upc-migration/export/${exportId}/status`);
          const status = statusResponse.data;
          
          if (status.status === 'completed') {
            // Download the file
            const downloadResponse = await apiClient.get(`/upc-migration/export/${exportId}/download`, {
              responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `migration_results_${new Date().toISOString().slice(0,10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            setSuccessMessage('Export downloaded successfully');
            exportComplete = true;
          } else if (status.status === 'failed') {
            setError(`Export failed: ${status.error_message || 'Unknown error'}`);
            exportComplete = true;
          }
          
          pollCount++;
        }
        
        if (!exportComplete) {
          setError('Export timeout. Please try again or use a smaller dataset.');
        }
        
      } else {
        // Use synchronous export for small result sets
        const response = await apiClient.post('/upc-migration/export-excel', results, {
          responseType: 'blob'
        });
        
        // Create a download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `migration_results_${new Date().toISOString().slice(0,10)}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        setSuccessMessage('Export downloaded successfully');
      }
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export results');
    }
  };

  const loadMigrationHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await apiClient.get('/upc-migration/batches');
      setMigrationHistory(response.data);
    } catch (err) {
      console.error('Error loading migration history:', err);
      setError('Failed to load migration history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleExportHistoricalBatch = async (batchId) => {
    try {
      const response = await apiClient.get(`/upc-migration/batch/${batchId}/export-excel`, {
        responseType: 'blob'
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `migration_batch_${batchId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccessMessage('Export downloaded successfully');
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export batch');
    }
  };

  const handleViewBatchResults = async (batchId) => {
    try {
      const response = await apiClient.get(`/upc-migration/batch/${batchId}/results`);
      setResults(response.data);
      setShowHistory(false);
      setSuccessMessage('Loaded batch results');
    } catch (err) {
      console.error('Error loading batch results:', err);
      setError('Failed to load batch results');
    }
  };

  React.useEffect(() => {
    if (showHistory && migrationHistory.length === 0) {
      loadMigrationHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHistory]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          UPC Migration & Rebranding Tool
        </Typography>
        <Button
          variant="outlined"
          onClick={() => setShowHistory(!showHistory)}
          startIcon={showHistory ? <PlayArrowIcon /> : <DownloadIcon />}
        >
          {showHistory ? 'New Migration' : 'View History'}
        </Button>
      </Box>
      
      {!showHistory ? (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" paragraph>
              This tool migrates products from old UPCs to new UPCs, pulling data from Syndigo first (if available) 
              or the Kroger API, creating products in Syndigo, and generating MRHIs with the new branding.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Note:</strong> UPCs can be entered in various formats (10, 12, 13, or 14 digits). 
              They will be automatically normalized to 14-digit GTINs.
            </Typography>
            <Typography variant="body2">
              <strong>Tip:</strong> Your migration results are automatically saved and can be accessed later from the "View History" button.
            </Typography>
          </Alert>
        </>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            View and export your previous migration batches. Click on any batch to view its results or export to Excel.
          </Typography>
        </Alert>
      )}

      {migrationProgress && (
        <Card sx={{ mb: 2, bgcolor: 'info.main', color: 'white' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Migration Progress
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={migrationProgress.progress_percentage || 0} 
                  sx={{ 
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: 'white'
                    }
                  }}
                />
              </Box>
              <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2">
                  {Math.round(migrationProgress.progress_percentage || 0)}%
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2">
              Processing: {migrationProgress.processed_count || 0} / {migrationProgress.total_count || 0} items
            </Typography>
            {migrationProgress.success_count !== undefined && (
              <Typography variant="body2">
                ✓ Success: {migrationProgress.success_count}
              </Typography>
            )}
            {migrationProgress.failed_count !== undefined && migrationProgress.failed_count > 0 && (
              <Typography variant="body2">
                ✗ Failed: {migrationProgress.failed_count}
              </Typography>
            )}
            {migrationProgress.partial_count !== undefined && migrationProgress.partial_count > 0 && (
              <Typography variant="body2">
                ⚠ Partial: {migrationProgress.partial_count}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}
      
      {partialSuccess && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setPartialSuccess('')}>
          {partialSuccess}
        </Alert>
      )}

      {!showHistory ? (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Migration Settings
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={isProcessing ? <CircularProgress size={20} /> : <PlayArrowIcon />}
              onClick={handleProcessMigration}
              disabled={isProcessing || migrations.length === 0 || !migrations[0].old_upc}
            >
              {isProcessing ? (
                migrationProgress ? 
                  `Processing ${migrationProgress.processed_count || 0}/${migrationProgress.total_count || 0}...` : 
                  'Processing...'
              ) : 'Process Migration'}
            </Button>
          </Box>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Default Brand"
            value={brandOverride}
            onChange={(e) => setBrandOverride(e.target.value)}
            size="small"
            sx={{ width: 200 }}
            helperText="Default for all items"
          />
          
          <TextField
            label="Strip Supplier Names"
            value={supplierNamesToStrip}
            onChange={(e) => setSupplierNamesToStrip(e.target.value)}
            size="small"
            sx={{ width: 300 }}
            helperText="Comma-separated (e.g., Japanese Food Express, Supplier Inc)"
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={createInSyndigo}
                onChange={(e) => setCreateInSyndigo(e.target.checked)}
              />
            }
            label="Create in Syndigo"
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={generateMRHI}
                onChange={(e) => setGenerateMRHI(e.target.checked)}
              />
            }
            label="Generate MRHI"
          />
        </Box>
        
        {createInSyndigo && (
          <Box sx={{ ml: 3, mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allowOverwrite}
                  onChange={(e) => setAllowOverwrite(e.target.checked)}
                  color="warning"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>Allow Overwrite</Typography>
                  <Typography variant="caption" color="text.secondary">
                    (Update existing products in Syndigo)
                  </Typography>
                </Box>
              }
            />
            {allowOverwrite && (
              <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
                <Typography variant="body2">
                  <strong>Warning:</strong> This will update existing products in Syndigo if the new UPC already exists.
                  Only enable this if you intend to overwrite existing product data.
                </Typography>
              </Alert>
            )}
          </Box>
        )}
        
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            size="small"
          >
            Download Template
          </Button>
          
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
            size="small"
          >
            Upload CSV
            <input
              type="file"
              accept=".csv"
              hidden
              onChange={handleFileUpload}
            />
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />
        
        <Typography variant="h6" gutterBottom>
          UPC Pairs
        </Typography>
        
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Old UPC (Source)</TableCell>
                <TableCell>New UPC (Target)</TableCell>
                <TableCell>Brand Override (Optional)</TableCell>
                <TableCell>Description Override (Optional)</TableCell>
                <TableCell width={60}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {migrations.map((migration, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextField
                      value={migration.old_upc}
                      onChange={(e) => handleUpdateRow(index, 'old_upc', e.target.value)}
                      placeholder="Enter old UPC"
                      size="small"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={migration.new_upc}
                      onChange={(e) => handleUpdateRow(index, 'new_upc', e.target.value)}
                      placeholder="Enter new UPC"
                      size="small"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={migration.brand_override}
                      onChange={(e) => handleUpdateRow(index, 'brand_override', e.target.value)}
                      placeholder="Use default"
                      size="small"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={migration.description_override}
                      onChange={(e) => handleUpdateRow(index, 'description_override', e.target.value)}
                      placeholder="Use original"
                      size="small"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleRemoveRow(index)}
                      size="small"
                      disabled={migrations.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddRow}
            size="small"
          >
            Add Row
          </Button>
        </Box>
      </Paper>
      ) : (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Migration History
          </Typography>
          {loadingHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : migrationHistory.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No migration history found
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell align="center">Total</TableCell>
                    <TableCell align="center">Success</TableCell>
                    <TableCell align="center">Partial</TableCell>
                    <TableCell align="center">Failed</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {migrationHistory.map((batch) => (
                    <TableRow key={batch.id}>
                      <TableCell>
                        {new Date(batch.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>{batch.brand_override}</TableCell>
                      <TableCell align="center">{batch.total_count}</TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={batch.success_count} 
                          color="success" 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell align="center">
                        {batch.partial_count > 0 && (
                          <Chip 
                            label={batch.partial_count} 
                            color="warning" 
                            size="small" 
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {batch.failed_count > 0 && (
                          <Chip 
                            label={batch.failed_count} 
                            color="error" 
                            size="small" 
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleViewBatchResults(batch.id)}
                          >
                            View
                          </Button>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleExportHistoricalBatch(batch.id)}
                            title="Export to Excel"
                          >
                            <GetAppIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {results.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Migration Results
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<GetAppIcon />}
              onClick={handleExportResults}
              size="small"
            >
              Export to Excel
            </Button>
          </Box>
          
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Old UPC</TableCell>
                  <TableCell>New UPC</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Product Info</TableCell>
                  <TableCell>Syndigo</TableCell>
                  <TableCell>Image ID</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((result, index) => (
                  <TableRow key={index}>
                    <TableCell>{result.old_upc}</TableCell>
                    <TableCell>{result.new_upc}</TableCell>
                    <TableCell>
                      <Chip 
                        label={result.status} 
                        color={result.status === 'success' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {result.kroger_data?.description && (
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {result.kroger_data.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {result.syndigo_created && (
                        <Chip label="Created" color="primary" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {result.image_id && (
                        <Button
                          size="small"
                          onClick={() => window.location.href = `/images/${result.image_id}`}
                        >
                          {result.image_id}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      {result.error && (
                        <Typography variant="body2" color="error" sx={{ fontSize: '0.8rem' }}>
                          {result.error}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default UPCMigrationPage;